import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Card } from "@cofano/cck";
import { Box, Stack, Typography, Grid2 as Grid, Collapse } from "@mui/material";

import { DataFormat, hasPluginPermission, Language } from "imports/Utils";

import { FormattedDateWindow } from "utils/DateUtils";
import { LocationLink } from "utils/LinkUtils";

import { calculateCallStatus } from "view/calls/utils/CallUtils";
import CallContainersAmount from "view/calls/components/amount/CallContainersAmount";

import { getCallProductsInfo, getRouteBetweenCalls, ProductInformation, VoyagePlanCallEvent, VoyagePlanEvent } from "../../AnglerUtils";
import AnglerTimelineRouteEvent from "./AnglerTimelineRouteEvent";

import { CallType } from "__generated__/enums";
import { GetCargosForVoyagePlanDocument } from "__generated__/view/cargos/graphql/cargo.queries.generated";

export const AnglerTimelineCallEvent = ({
	event,
	events,
	allTagsDisabled,
}: {
	event: VoyagePlanCallEvent;
	events: VoyagePlanEvent[];
	allTagsDisabled: boolean;
}) => {
	const session = useStoreState(store => store.session);
	const hasContainerPermission = hasPluginPermission(session, "useContainers");
	const call = event.referable;

	const [ dischargingExpanded, setDischargingExpanded ] = useState(false);
	const [ loadingExpanded, setLoadingExpanded ] = useState(false);

	if (!call || !call.id) {
		return <React.Fragment />;
	}

	const { data } = useQuery(GetCargosForVoyagePlanDocument, {
		variables: {
			pagination: { page: 0, size: 500 },
			loadingSearch: { loadingCalls: [ call.id ] },
			dischargingSearch: { dischargingCalls: [ call.id ] },
		},
	});

	const dischargingProducts = getCallProductsInfo(call, data?.dischargingCargos?.content ?? [], CallType.Discharging);
	const loadingProducts =  getCallProductsInfo(call, data?.loadingCargos?.content ?? [], CallType.Loading);

	const status = calculateCallStatus(call, session);
	const route = getRouteBetweenCalls(event, events);

	return (
		<>
			<Card disablePadding>
				<Box sx={{ p: 1.5, pl: 4.5, position: "relative" }}>
					<Stack>
						<Grid spacing={3} container>
							<Grid size={{ xs: 12, md: 4 }}>
								<Typography variant="h6" color="textPrimary" sx={{ fontSize: 14, mb: 0 }}>
									{call.location?.id && call.location?.description ?
										<LocationLink location={{ id: call.location.id, description: call.location.description }} /> :
										intl.formatMessage({ id: "UNKNOWN" })}
								</Typography>

								<Typography variant="h6" color="textSecondary" sx={{ fontSize: 13 }}>
									{call.location?.address?.town || call.location?.code || intl.formatMessage({ id: "UNKNOWN" })}
								</Typography>

								<Stack spacing={0.5} mt={1}>
									{[
										{ label: "PLANNING", from: call.pta, to: call.ptd },
										{ label: "EXPECTED", from: call.eta, to: call.etd },
										{ label: "ACTUAL", from: call.ata, to: call.atd },
									].map((row) => (
										<Box
											key={row.label}
											sx={{
												alignItems: "center",
												display: "flex",
												position: "relative",
											}}>
											<Typography
												color="text.disabled"
												sx={{
													fontSize: 10,
													fontWeight: 700,
													textTransform: "uppercase",
													letterSpacing: 0.5,
													width: 80,
												}}>
												{intl.formatMessage({ id: row.label })}
											</Typography>

											<Typography
												color="text.secondary"
												sx={{
													fontSize: 10,
													fontWeight: 600,
													whiteSpace: "nowrap",
												}}>
												<FormattedDateWindow from={row.from} to={row.to} />
											</Typography>
										</Box>
									))}
								</Stack>
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								{hasContainerPermission ? (
									<Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800, mb: 1 }}>
										{Language("CALL_TYPE_1")}
									</Typography>
								) : (
									<Box
										onClick={() => dischargingProducts.length > 0 && setDischargingExpanded(!dischargingExpanded)}
										sx={{
											alignItems: "center",
											cursor: dischargingProducts.length > 0 ? "pointer" : "default",
											display: "flex",
											justifyContent: "space-between",
											mb: 1,
											userSelect: "none",

											"&:hover p": {
												color: dischargingProducts.length > 0 ? "primary.main" : "text.secondary",
											},
										}}>
										<Typography
											sx={{
												fontSize: 12,
												color: "text.secondary",
												fontWeight: 800,
												transition: "color 0.15s",
											}}>
											{Language("CALL_TYPE_1")} {dischargingProducts.length > 0 && (dischargingExpanded ? "▴" : "▾")}
										</Typography>
									</Box>
								)}

								{hasContainerPermission ? (
									<CallContainersAmount call={call} loadingOrDischarging={CallType.Discharging} compact  />
								) : (
									<CargoSummary products={dischargingProducts} expanded={dischargingExpanded} />
								)}
							</Grid>

							<Grid size={{ xs: 12, sm: 6, md: 4 }}>
								{hasContainerPermission ? (
									<Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800, mb: 1 }}>
										{Language("CALL_TYPE_0")}
									</Typography>
								) : (
									<Box
										onClick={() => loadingProducts.length > 0 && setLoadingExpanded(!loadingExpanded)}
										sx={{
											alignItems: "center",
											cursor: loadingProducts.length > 0 ? "pointer" : "default",
											display: "flex",
											justifyContent: "space-between",
											mb: 1,
											userSelect: "none",

											"&:hover p": {
												color: loadingProducts.length > 0 ? "primary.main" : "text.secondary",
											},
										}}>
										<Typography
											sx={{
												fontSize: 12,
												color: "text.secondary",
												fontWeight: 800,
												transition: "color 0.15s",
											}}>
											{Language("CALL_TYPE_0")} {loadingProducts.length > 0 && (loadingExpanded ? "▴" : "▾")}
										</Typography>
									</Box>
								)}

								{hasContainerPermission ? (
									<CallContainersAmount call={call} loadingOrDischarging={CallType.Loading} compact />
								) : (
									<CargoSummary products={loadingProducts} expanded={loadingExpanded} />
								)}
							</Grid>
						</Grid>
					</Stack>

					<Box
						sx={{
							alignItems: "center",
							backgroundColor: status.color,
							bottom: 0,
							color: "#FFF",
							display: "flex",
							justifyContent: "center",
							left: 0,
							position: "absolute",
							top: 0,
							width: 24,

						}}>
						{status.symbol}
					</Box>
				</Box>
			</Card>

			{route.nextCall && allTagsDisabled && (
				<AnglerTimelineRouteEvent call={call} route={route} />
			)}
		</>
	);
};

const CargoSummary = ({ products, expanded }: { products: ProductInformation[]; expanded: boolean }) => {
	if (products.length === 0) {
		return <Typography sx={{ fontSize: 11, color: "text.disabled", fontStyle: "italic", pt: 0.5 }}>-</Typography>;
	}

	return (
		<Stack spacing={1} sx={{ width: "100%" }}>
			{products.map((item, index) => <CargoItem key={index} item={item} expanded={expanded} />)}
		</Stack>
	);
};

const CargoItem = ({ item, expanded }: { item: ProductInformation; expanded: boolean }) => {
	const locStr = `${DataFormat.simpleUnit(item.locationVolumeL15, DataFormat.UNITS.L15)} / ${DataFormat.simpleUnit(item.locationWeightMTon, DataFormat.UNITS.MTON)}`;
	const skipStr = `${DataFormat.simpleUnit(item.skipperVolumeL15, DataFormat.UNITS.L15)} / ${DataFormat.simpleUnit(item.skipperWeightMTon, DataFormat.UNITS.MTON)}`;

	return (
		<Box
			sx={{
				borderRadius: 1,
				display: "flex",
				flexDirection: "column",
				width: "100%",
			}}>
			<Box
				sx={{
					alignItems: "flex-start",
					display: "flex",
					justifyContent: "space-between",
				}}>
				<Box>
					<Typography
						sx={{
							color: "text.primary",
							fontSize: 11,
							fontWeight: 700,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}>
						{item.product?.name}
					</Typography>

					<Typography
						sx={{
							color: "text.secondary",
							fontSize: 10,
							fontWeight: 600,
							mt: 0.25,
						}}>
						{DataFormat.tonnage(item.weight)} • {DataFormat.volumeM3(item.volume)}
					</Typography>
				</Box>
			</Box>

			<Collapse in={expanded} timeout="auto" unmountOnExit>
				<Stack mt={0.5}>
					{[
						{ label: "Land", string: locStr },
						{ label: "Ship", string: skipStr },
					].map((row, rIndex) => (
						<Box
							key={rIndex}
							sx={{
								alignItems: "center",
								display: "flex",
								justifyContent: "space-between",
								gap: 1,
								width: "100%",
							}}>
							<Box>
								<Typography
									color="text.secondary"
									sx={{
										fontSize: 10,
										fontWeight: 700,
										letterSpacing: "0.3",
									}}>
									{row.label}
								</Typography>
							</Box>

							<Typography
								sx={{
									color: "text.secondary",
									fontSize: 10,
									fontWeight: 600,
									fontFamily: "monospace",
									overflow: "hidden",
									textOverflow: "ellipsis",
									textAlign: "right",
									whiteSpace: "nowrap",
								}}>
								{row.string}
							</Typography>
						</Box>
					))}
				</Stack>
			</Collapse>
		</Box>
	);
};

export default AnglerTimelineCallEvent;
